/**
 * DrawingPanel: 스케치 모드 패널
 *
 * 원본 wehago-conference RenewalComponents/Drawing 구조와 동일:
 * - 발표자(reduxIsSketchOwner): DrawingContainer 패턴 — SketchField ref + ToolBox + DocumentDrawing
 * - 시청자(!reduxIsSketchOwner): SketchField value prop으로 읽기전용 표시
 *
 * sendCanvasData 호출 포맷:
 *   { documentData: extractionObject(canvasJSON), dataURL, canvasType: 'sketch', from: 'web' }
 */
import React, { Component, createRef, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { drawingActions } from '@/store/slices/drawingSlice';
import { SketchField, Tools } from '@/components/SketchField';
// @ts-ignore
import DocumentDrawing from '@/utils/documentDrawing';
import './DrawingPanel.css';

// ── 색상 목록 (원본과 동일) ──────────────────────────────────────────────────
const COLOR_LIST = [
  { id: 1, color: '#000000', name: '검정', isBlackChecker: true },
  { id: 2, color: '#FF0000', name: '빨강', isBlackChecker: false },
  { id: 3, color: '#0000FF', name: '파랑', isBlackChecker: false },
  { id: 4, color: '#008000', name: '초록', isBlackChecker: false },
  { id: 5, color: '#FFFF00', name: '노랑', isBlackChecker: false },
  { id: 6, color: '#FF00FF', name: '보라', isBlackChecker: false },
  { id: 7, color: '#FFFFFF', name: '흰색', isBlackChecker: false },
];

const MIN_LINE_WIDTH = 1;
const MAX_LINE_WIDTH = 20;

interface DrawingPanelProps {
  sendCanvasData: (sketchData: object, x: number, y: number) => void;
  onStopSketch?: () => void;
  onClearDrawingCanvas?: () => void;
  isHidden?: boolean;
}

// ── 발표자 뷰 ────────────────────────────────────────────────────────────────
interface DrawingSendState {
  color: string;
  lineWidth: number;
  tool: string;
  canUndo: boolean;
  canRedo: boolean;
  showPentool: boolean;
}

class DrawingSend extends Component<{ sendCanvasData: DrawingPanelProps['sendCanvasData']; onStopSketch?: () => void; onClearDrawingCanvas?: () => void; isHidden?: boolean; }, DrawingSendState> {
  canvas: React.RefObject<any>;
  canvas_element: React.RefObject<HTMLDivElement | null>;
  _drawingManager: any;
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** 정규화 좌표 백업 — 리사이즈 시 복원용 */
  _normalizedBackup: any = null;

  constructor(props: { sendCanvasData: DrawingPanelProps['sendCanvasData'] }) {
    super(props);
    this.canvas = createRef();
    this.canvas_element = createRef();
    this.containerRef = createRef();
    this._drawingManager = new DocumentDrawing();
    this.state = {
      color: '#000000',
      lineWidth: 5,
      tool: Tools.Pencil,
      canUndo: false,
      canRedo: false,
      showPentool: false,
    };
  }

  componentDidMount() {
    const el = this.canvas_element.current;
    if (el) {
      this._drawingManager.width = el.offsetWidth;
      this._drawingManager.height = el.offsetHeight;
    }
  }

  componentWillUnmount() {
    this.clearCanvas();
  }

  clearCanvas = () => {
    this._normalizedBackup = null;
    if (this.canvas.current) this.canvas.current.clear();
  };

  /** 현재 캔버스를 정규화 좌표로 백업 */
  backupNormalized = () => {
    if (!this.canvas.current) return;
    const canvasData = this.canvas.current.toJSON();
    if (canvasData.objects && canvasData.objects.length > 0) {
      this._normalizedBackup = this._drawingManager.extractionObject(canvasData);
    }
  };

  /** 정규화 백업에서 새 크기로 복원 */
  restoreFromBackup = (newWidth: number, newHeight: number) => {
    if (!this._normalizedBackup || !this.canvas.current) return;
    this._drawingManager.width = newWidth;
    this._drawingManager.height = newHeight;
    const restored = this._drawingManager.convertFromWebData(this._normalizedBackup);
    const fc = this.canvas.current._fc;
    if (fc) {
      fc.loadFromJSON(restored, () => {
        fc.renderAll();
      });
    }
  };

  sendCanvasData = (documentData: object, dataURL: string) => {
    this.props.sendCanvasData({ documentData, dataURL, canvasType: 'sketch', from: 'web' }, 0, 0);
  };

  onSketchChange = (e: any) => {
    if (!this.canvas.current) return;
    const canvas = this.canvas.current;
    if (e && e.type === 'mouseout') return;

    const canUndo = canvas.canUndo();
    const canRedo = canvas.canRedo();
    this.setState({ canUndo, canRedo });

    const canvasData = canvas.toJSON();
    const dataURL = canvas.toDataURL();
    const documentData = this._drawingManager.extractionObject(canvasData);

    canvas._fc && canvas._fc.renderAll();
    this.sendCanvasData(documentData, dataURL);
  };

  handleUndo = () => {
    if (this.canvas.current && this.canvas.current.canUndo()) {
      this.canvas.current.undo();
      this.setState({
        canUndo: this.canvas.current.canUndo(),
        canRedo: this.canvas.current.canRedo(),
      });
      this.onSketchChange({});
    }
  };

  handleRedo = () => {
    if (this.canvas.current && this.canvas.current.canRedo()) {
      this.canvas.current.redo();
      this.setState({
        canUndo: this.canvas.current.canUndo(),
        canRedo: this.canvas.current.canRedo(),
      });
      this.onSketchChange({});
    }
  };

  render() {
    const { color, lineWidth, tool, canUndo, canRedo, showPentool } = this.state;

    return (
      <DrawingSendView
        canvasRef={this.canvas}
        canvasElementRef={this.canvas_element}
        color={color}
        lineWidth={lineWidth}
        tool={tool}
        canUndo={canUndo}
        canRedo={canRedo}
        showPentool={showPentool}
        onUndo={this.handleUndo}
        onRedo={this.handleRedo}
        onClear={this.clearCanvas}
        onSketchChange={this.onSketchChange}
        onColorChange={(c: string) => this.setState({ color: c })}
        onLineWidthChange={(w: number) => this.setState({ lineWidth: w })}
        onToolChange={(t: string) => this.setState({ tool: t })}
        onTogglePentool={() => this.setState((s) => ({ showPentool: !s.showPentool }))}
        onClosePentool={() => this.setState({ showPentool: false })}
        onStopSketch={this.props.onStopSketch}
        onClearDrawingCanvas={this.props.onClearDrawingCanvas}
        isHidden={this.props.isHidden}
        onBackupNormalized={this.backupNormalized}
        onRestoreFromBackup={this.restoreFromBackup}
      />
    );
  }
}

// ── 발표자 뷰 프레젠터 ───────────────────────────────────────────────────────
interface DrawingSendViewProps {
  canvasRef: React.RefObject<any>;
  canvasElementRef: React.RefObject<HTMLDivElement | null>;
  color: string;
  lineWidth: number;
  tool: string;
  canUndo: boolean;
  canRedo: boolean;
  showPentool: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onClear: () => void;
  onSketchChange: (e: any) => void;
  onColorChange: (c: string) => void;
  onLineWidthChange: (w: number) => void;
  onToolChange: (t: string) => void;
  onTogglePentool: () => void;
  onClosePentool: () => void;
  onStopSketch?: () => void;
  onClearDrawingCanvas?: () => void;
  isHidden?: boolean;
  onBackupNormalized: () => void;
  onRestoreFromBackup: (w: number, h: number) => void;
}

const DrawingSendView: React.FC<DrawingSendViewProps> = ({
  canvasRef, canvasElementRef, color, lineWidth, tool, canUndo, canRedo, showPentool,
  onUndo, onRedo, onClear, onSketchChange, onColorChange, onLineWidthChange,
  onToolChange, onTogglePentool, onClosePentool, onStopSketch, onClearDrawingCanvas, isHidden,
  onBackupNormalized, onRestoreFromBackup
}) => {
  const dispatch = useAppDispatch();
  const [containerSize, setContainerSize] = React.useState<{ w: number; h: number } | null>(null);
  const isInitialMount = useRef(true);

  // 리사이즈 시: 정규화 백업 → 크기 업데이트 → 복원
  React.useLayoutEffect(() => {
    const el = canvasElementRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) {
        // 초기 마운트 시에는 백업 불요
        if (!isInitialMount.current) {
          onBackupNormalized();
        }
        isInitialMount.current = false;
        setContainerSize({ w: width, h: height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // containerSize 변경 후 복원 (SketchField가 새 크기로 리사이즈된 뒤)
  React.useEffect(() => {
    if (!containerSize) return;
    // SketchField의 componentDidUpdate → _resize() 완료 후 복원
    const timer = setTimeout(() => {
      onRestoreFromBackup(containerSize.w, containerSize.h);
    }, 50);
    return () => clearTimeout(timer);
  }, [containerSize]);

  // SketchField는 PureComponent — style 객체가 매번 새로 생성되면 불필요한 리렌더 발생
  const canvasStyle = useMemo(() => ({
    position: 'absolute' as const,
    zIndex: 2,
    left: 0,
    right: 0,
    top: 0,
  }), []);

  const handleExit = () => {
    dispatch(drawingActions.setIsSketchShare(false));
    dispatch(drawingActions.setIsSketchOwner(false));
    dispatch(drawingActions.setSketchOwner(''));
    dispatch(drawingActions.setSketchData(null)); // BUG-6 fix: 이전 스케치 데이터 초기화
    onStopSketch?.(); // 다른 참여자에게 스케치 모드 종료 알림
  };

  return (
    <div className="file_viewer" style={{ backgroundColor: '#FFF', zIndex: 1001, display: isHidden ? 'none' : 'block' }}>
      <div className="curpointer" />

      {/* ── Toolbar (viewer_head) ── */}
      <div className="viewer_head clearfix" style={{ backgroundColor: '#FFF', zIndex: 1300}}>
        <div className="btngroup_wrap">
          {/* Undo */}
          <button
            type="button"
            className="LUX_basic_btn Default basic"
            title="실행취소"
            disabled={!canUndo}
            style={{ opacity: canUndo ? 1 : 0.4 }}
            onClick={onUndo}
          >
            <span className="sp_rtc" style={{ backgroundPosition: '-132px -401px' }}>실행취소</span>
          </button>

          {/* Redo */}
          <button
            type="button"
            className="LUX_basic_btn Default basic"
            title="다시실행"
            disabled={!canRedo}
            style={{ opacity: canRedo ? 1 : 0.4 }}
            onClick={onRedo}
          >
            <span className="sp_rtc" style={{ backgroundPosition: '-168px -401px' }}>다시실행</span>
          </button>

          {/* Pentool button + dropdown */}
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button
              type="button"
              className="LUX_basic_btn Default basic btn_pentool"
              title="펜툴"
              onClick={onTogglePentool}
            >
              <span className="sp_rtc" style={{ backgroundPosition: '-204px -401px' }}>펜툴</span>
            </button>

            {showPentool && (
              <>
                {/* backdrop */}
                <div
                  style={{ position: 'fixed', inset: 0, zIndex: 100 }}
                  onClick={onClosePentool}
                />
                <div
                  className="pentool_layer"
                  style={{ display: 'block', position: 'absolute', top: 44, right: 0, left: 'auto', zIndex: 101 }}
                >
                  {/* Tool type selection */}
                  <ul className="top_menu">
                    <li>
                      <button type="button" className={`pen_pencil${tool === Tools.Pencil ? ' active' : ''}`} onClick={() => { onToolChange(Tools.Pencil); }}>
                        <span className="sp_rtc" />
                      </button>
                    </li>
                    <li>
                      <button type="button" className={`pen_eraser${tool === Tools.Select ? ' active' : ''}`} onClick={() => { onToolChange(Tools.Select); }}>
                        <span className="sp_rtc" />
                      </button>
                    </li>
                  </ul>

                  <div className="bottom_content">
                    {tool === Tools.Pencil ? (
                      <>
                        {/* Color picker */}
                        <div className="colorbx_set">
                          <ul>
                            {COLOR_LIST.map((item) => (
                              <li key={item.id}>
                                <span>
                                  <input
                                    type="radio"
                                    id={`input_color_picker${item.id}`}
                                    name="input_color_picker"
                                    checked={item.color === color}
                                    onChange={() => onColorChange(item.color)}
                                  />
                                  <label
                                    htmlFor={`input_color_picker${item.id}`}
                                    className={`pick_color${item.isBlackChecker ? ' black_checker' : ''}`}
                                    style={{ background: item.color }}
                                  >
                                    {item.name}
                                  </label>
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Line width */}
                        <div className="range_set">
                          <button
                            type="button"
                            className="range_btn"
                            onClick={() => onLineWidthChange(Math.max(MIN_LINE_WIDTH, lineWidth - 1))}
                          >-</button>
                          <input
                            type="range"
                            min={MIN_LINE_WIDTH}
                            max={MAX_LINE_WIDTH}
                            step={1}
                            value={lineWidth}
                            onChange={(e) => onLineWidthChange(Number(e.target.value))}
                          />
                          <button
                            type="button"
                            className="range_btn"
                            onClick={() => onLineWidthChange(Math.min(MAX_LINE_WIDTH, lineWidth + 1))}
                          >+</button>
                          <span className="range_value">{lineWidth}</span>
                        </div>
                      </>
                    ) : (
                      /* Erase All */
                      <div className="btn_erase_all">
                        <button
                          type="button"
                          onClick={() => { onClear(); onClearDrawingCanvas?.(); onClosePentool(); }}
                        >
                          전체 지우기
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Exit */}
          <button
            type="button"
            className="LUX_basic_btn Default basic btn_fileclose"
            title="스케치 종료"
            onClick={handleExit}
          >
            <span className="sp_rtc" style={{ backgroundPosition: '-348px -401px' }}>종료</span>
          </button>
        </div>
      </div>

      {/* ── Canvas (viewer_contents) — CSS: position:absolute, padding: 61px 15px 15px 15px ── */}
      <div
        className="viewer_contents"
        ref={canvasElementRef}
      >
        {containerSize && (
          <SketchField
            ref={canvasRef}
            tool={tool}
            width={containerSize.w}
            height={containerSize.h}
            lineColor={color}
            fillColor="#FFF"
            lineWidth={lineWidth}
            onChange={onSketchChange}
            style={canvasStyle}
          />
        )}
      </div>
    </div>
  );
};

// ── 시청자 뷰 ────────────────────────────────────────────────────────────────
const DrawingGet: React.FC<{
  sketchData: object | null;
  sketchWidthLimit: number;
  sketchHeightLimit: number;
}> = ({ sketchData, sketchWidthLimit, sketchHeightLimit }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<any>(null);
  const [containerSize, setContainerSize] = useState<{ w: number; h: number } | null>(null);
  const _drawingManager = useRef(new DocumentDrawing());

  const canvasStyle = useMemo(() => ({
    position: 'absolute' as const,
    zIndex: 2,
    marginLeft: 'auto',
    marginRight: 'auto',
    left: 0,
    right: 0,
    top: 0,
  }), []);

  // 컨테이너 크기 설정 — BUG-2 fix: ResizeObserver로 실제 크기 확보 후 측정
  React.useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) {
        _drawingManager.current.width = width;
        _drawingManager.current.height = height;
        setContainerSize({ w: width, h: height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // 수신 데이터 처리 (web → convertFromWebData)
  // 수신 데이터 구조: { documentData: { objects: [...], version }, dataURL, canvasType, from }
  // documentData 안에 실제 캔버스 JSON이 있음
  // NOTE: loadFromJSON만 사용하고 setCanvasValue는 사용하지 않음
  //       (value prop과 직접 loadFromJSON을 동시에 사용하면 double-loading으로 캔버스가 깜빡임)
  useEffect(() => {
    if (!sketchData) return;
    const raw = sketchData as any;
    // documentData가 있으면 그 안의 캔버스 JSON 사용, 아니면 직접 objects 확인
    const canvasJSON = raw.documentData?.objects ? raw.documentData : raw.objects ? raw : null;
    if (!canvasJSON) return;

    const dm = _drawingManager.current;
    if (!dm.width || !dm.height) {
      const el = containerRef.current;
      if (el) { dm.width = el.offsetWidth; dm.height = el.offsetHeight; }
    }

    let dataToLoad: any;
    try {
      dataToLoad = dm.setDataFromWeb(canvasJSON);
    } catch {
      dataToLoad = canvasJSON;
    }

    const fc = canvasRef.current?._fc;
    if (fc) {
      fc.loadFromJSON(dataToLoad, () => {
        fc.renderAll();
        fc.forEachObject((obj: any) => {
          obj.selectable = false;
          obj.hoverCursor = 'grab';
        });
      });
    }
  }, [sketchData, containerSize]);

  return (
    <div className="file_viewer" style={{ background: '#FFF', zIndex: 1001 }}>
      <div className="curpointer" />
      <div className="viewer_contents" ref={containerRef}>
        {containerSize && (
          <SketchField
            ref={canvasRef}
            tool={Tools.Nomal}
            lineColor="transparent"
            fillColor="transparent"
            width={containerSize.w}
            height={containerSize.h}
            style={canvasStyle}
          />
        )}
      </div>
    </div>
  );
};

// ── 메인 진입점 ───────────────────────────────────────────────────────────────
// DrawingPanel: 발표자(owner)일 때만 DrawingSend 오버레이를 렌더링
const DrawingPanel: React.FC<DrawingPanelProps> = ({ sendCanvasData, onStopSketch, onClearDrawingCanvas, isHidden }) => {
  const drawing = useAppSelector((s) => s.drawing);

  if (!drawing.reduxIsSketchShare) return null;
  if (!drawing.reduxIsSketchOwner) return null;

  return <DrawingSend sendCanvasData={sendCanvasData} onStopSketch={onStopSketch} onClearDrawingCanvas={onClearDrawingCanvas} isHidden={isHidden} />;
};

export { DrawingGet };
export default DrawingPanel;
